import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PermissionsEnum, UserStore } from '@emilegal/emi-fe-common';
import { ComplaintService } from './complaint.service';

export interface PinnedCase { id: string; name: string }

// The signed-in user's pinned cases, for the sidebar. Loaded once the user is known and
// again after a case is pinned, unpinned or renamed.
@Injectable({ providedIn: 'root' })
export class PinnedCases {
  private api = inject(ComplaintService);
  private userStore = inject(UserStore);
  private list = signal<PinnedCase[]>([]);
  private request = 0;
  readonly pinned = this.list.asReadonly();
  // Cases need the firm execute permission. Without it the API would only answer 403, so the
  // sidebar entry is hidden and nothing is asked for.
  readonly available = computed(() =>
    !!this.userStore.userInfo()?.permissions?.includes(PermissionsEnum.agentExternalExecute));

  constructor() {
    effect(() => {
      if (this.available()) untracked(() => void this.reload());
      else { this.request++; this.list.set([]); }
    });
  }

  async reload() {
    if (!this.available()) return;
    const request = ++this.request;
    try {
      const response = await firstValueFrom(this.api.pinned());
      // A slower earlier answer must not replace a newer one.
      if (request === this.request && response.isSuccess) {
        this.list.set(response.data.projects.map(p => ({ id: p.project_id, name: p.name })));
      }
    } catch { /* the sidebar keeps the list it has */ }
  }
}
